import {
  format,
  startOfDay,
  endOfDay,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subWeeks,
  subMonths,
  subYears,
} from 'date-fns';
import type { DateRange } from '@daypicker/react';

export type DateRangeValue = [Date | undefined, Date | undefined];

export type Preset = {
  label: string;
  getRange: () => DateRangeValue;
};

const weekOptions = { weekStartsOn: 1 as const };

export const getPresets = (): Preset[] => {
  const now = new Date();

  return [
    {
      label: 'Today',
      getRange: () => [startOfDay(now), endOfDay(now)],
    },
    {
      label: 'Yesterday',
      getRange: () => {
        const d = subDays(now, 1);
        return [startOfDay(d), endOfDay(d)];
      },
    },
    {
      label: 'This week',
      getRange: () => [
        startOfWeek(now, weekOptions),
        endOfWeek(now, weekOptions),
      ],
    },
    {
      label: 'Last week',
      getRange: () => {
        const lastWeek = subWeeks(now, 1);
        return [
          startOfWeek(lastWeek, weekOptions),
          endOfWeek(lastWeek, weekOptions),
        ];
      },
    },
    {
      label: 'This month',
      getRange: () => [startOfMonth(now), endOfMonth(now)],
    },
    {
      label: 'Last month',
      getRange: () => {
        const lastMonth = subMonths(now, 1);
        return [startOfMonth(lastMonth), endOfMonth(lastMonth)];
      },
    },
    {
      label: 'This year',
      getRange: () => [startOfYear(now), endOfYear(now)],
    },
    {
      label: 'Last year',
      getRange: () => {
        const lastYear = subYears(now, 1);
        return [startOfYear(lastYear), endOfYear(lastYear)];
      },
    },
    {
      label: 'All time',
      getRange: () => [undefined, undefined],
    },
  ];
};

export const formatDate = (date: Date | string | undefined): string => {
  if (!date) return '00/00/0000';
  return format(new Date(date), 'MM/dd/yyyy');
};

export const isSameDay = (
  a: Date | undefined,
  b: Date | undefined
): boolean => {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

export const isActivePreset = (
  preset: Preset,
  from: Date | undefined,
  to: Date | undefined
): boolean => {
  const [pFrom, pTo] = preset.getRange();
  if (!pFrom && !pTo) return !from && !to;
  return isSameDay(pFrom, from) && isSameDay(pTo, to);
};

export const valueToDateRange = (
  value: DateRangeValue | undefined
): DateRange | undefined => {
  if (!value) return undefined;
  const [from, to] = value;
  if (!from && !to) return undefined;
  return { from, to };
};

export const dateRangeToValue = (
  range: DateRange | undefined
): DateRangeValue => {
  return [range?.from, range?.to];
};
